import { toast } from 'react-toastify';
import { Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getTranslatedKey } from '../../../../../Utils/TranslateUtils';
import { config } from '../../../../../../config/Config';

let currentFetch = null;

export async function getFeatureByFilter(layer, filter, page, sortBy) {
  if (currentFetch) {
    currentFetch.abort();
  }
  if (filter && filter.trim() === '') {
    filter = undefined;
  }
  let featuresPerPage = 20;
  let index = page * featuresPerPage;
  let name = layer.getExtension().Namespace + ':' + layer.getName();
  let filterUrl = filter ? `&CQL_FILTER=${filter}` : '';
  let urlStart = layer.getOnlineResource();
  const properties = layer
    ?.getProperties()
    .map((prop) => prop.name)
    .concat(layer.getExtension().Geometry)
    .join(',');

  let sortArr = [];
  if (sortBy) {
    for (const key in sortBy) {
      if (sortBy[key] === 'asc') {
        sortArr.push(key + '+A');
      } else if (sortBy[key] === 'desc') {
        sortArr.push(key + '+D');
      }
    }
  }

  let sort = `&sortBy=${sortArr.join()}`;

  let url = `${urlStart}?service=wfs&version=2.0.0&request=GetFeature&typename=${name}&outputFormat=application/json&count=${featuresPerPage}${filterUrl}&srsName=${config.EPSG_mapa}&startindex=${index}${sort}&propertyName=${properties}`;

  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  try {
    let resp = await fetch(url, { signal });
    currentFetch = null;
    return await resp.json();
  } catch (error) {
    toast.error(getTranslatedKey('error_taula'), { autoClose: 4000, pauseOnHover: true });
    return 'error';
  }
}

export async function getFeatureTitles(layer) {
  if (currentFetch) {
    currentFetch.abort();
  }
  const properties = layer
    ?.getProperties()
    .map((prop) => prop.name)
    .concat(layer.getExtension().Geometry)
    .join(',');

  let name = layer.getExtension().Namespace + ':' + layer.getName();
  let urlStart = layer.getOnlineResource();

  let url = `${urlStart}?service=wfs&version=${config.version_2_0}&request=GetFeature&typename=${name}&outputFormat=application/json&count=1&startindex=0&propertyName=${properties}`;

  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  try {
    let resp = await fetch(url, { signal });
    currentFetch = null;
    return await resp.json();
  } catch (error) {
    if (error.name !== 'AbortError') {
    }
  }
}

export async function getAutocomplete(layer, field, type, input, endsWidth) {
  if (currentFetch) {
    currentFetch.abort();
  }
  let properties = layer.getProperties();
  field = properties.find((prop) => prop.alias === field)?.name || field;
  let name = layer.getExtension().Namespace + ':' + layer.getName();
  let urlStart = layer.getOnlineResource();

  let filter =
    endsWidth === true
      ? `&CQL_FILTER=${field}%20ILIKE%20%27%25${input}%27`
      : type === 'number' || type === 'date'
      ? `&CQL_FILTER=${field}%20ILIKE%20%27${input}%25%27`
      : `&CQL_FILTER=${field}%20ILIKE%20%27%25${input}%25%27`;

  let url = `${urlStart}?service=wfs&version=${config.version_2_0}&request=GetFeature&typename=${name}&outputFormat=application/json&count=50${filter}`;

  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  try {
    let resp = await fetch(url, { signal });
    currentFetch = null;

    let respJson = await resp.json();
    let list = respJson.features.map((feature) => feature.properties[field]);
    let uniques = new Set(list);
    list = [...uniques];
    return list;
  } catch (error) {}
}

export async function getAllFeatures(layer, filter) {
  // let identificador = layer.getExtension().Id;
  if (filter && filter.trim() === '') {
    filter = undefined;
  }
  if (currentFetch) {
    currentFetch.abort();
  }
  const properties = layer
    ?.getProperties()
    .map((prop) => prop.name)
    .concat(layer.getExtension().Geometry)
    .join(',');

  let name = layer.getExtension().Namespace + ':' + layer.getName();
  let urlStart = layer.getOnlineResource();

  let url = `${urlStart}?service=wfs&version=2.0.0&request=GetFeature&typename=${name}&outputFormat=application/json&srsName=${config.EPSG_mapa}&propertyName=${properties}`;

  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  if (filter && filter !== '') {
    let CQLFilter = '&CQL_FILTER=' + filter;
    url += CQLFilter;
  }

  try {
    let resp = await fetch(url, { signal });
    currentFetch = null;
    return await resp.json();
  } catch (error) {}
}

export const getFieldsType = async (layer) => {
  let name = layer.getExtension().Namespace + ':' + layer.getName();
  const or = layer.getOnlineResource();

  let url = `${or}?REQUEST=DescribeFeatureType&VERSION=${config.version_2_0}&TYPENAME=${name}&SERVICE=WFS`;

  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  try {
    let resp = await fetch(url, { signal });
    const text = await resp.text();

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, 'text/xml');
    let complexTypes = xmlDoc.getElementsByTagNameNS('http://www.w3.org/2001/XMLSchema', 'complexType');

    const obj = {};
    const extension = layer.getExtension();

    Array.from(complexTypes).forEach((complexType) => {
      const elements = Array.from(complexType.getElementsByTagNameNS('http://www.w3.org/2001/XMLSchema', 'element'));
      elements.forEach((element) => {
        const elementName = element.getAttribute('name');
        const elementType = element.getAttribute('type');

        if (elementName && elementName !== extension.Geometry && elementName !== extension.Id && elementType) {
          obj[elementName] = elementType.replace('xsd:', '').replace('decimal', 'number').replace('dateTime', 'date');
        }
      });
    });

    currentFetch = null;
    return obj;
  } catch (error) {
    if (error.name !== 'AbortError') {
    }
  }
};

export const getTableHits = async (layer, filter) => {
  if (filter && filter.trim() === '') {
    filter = undefined;
  }
  let url = layer.getOnlineResource();
  let layerName = layer.getExtension().Namespace + ':' + layer.getName();

  url = `${url}?service=wfs&version=1.1.0&request=GetFeature&typename=${layerName}&resultType=hits`;

  if (filter && filter !== '') {
    let CQLFilter = '&CQL_FILTER=' + filter;
    url += CQLFilter;
  }

  try {
    let resp = await fetch(url);
    const xmlText = await resp.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    const numberOfFeatures = xmlDoc.documentElement.getAttribute('numberOfFeatures');
    return numberOfFeatures;
  } catch {}
};

export const statsForProperty = async (layer, field, filters) => {
  let name = layer.getExtension().Namespace + ':' + layer.getName();
  let urlStart = layer.getOnlineResource();
  let filtersUlr = ``;
  if (filters) {
    filtersUlr += `(${filters})%20AND%20${field}%20IS%20NOT%20NULL`;
  } else {
    filtersUlr += `${field}%20IS%20NOT%20NULL`;
  }
  let url = `${urlStart}?service=wfs&version=${config.version_2_0}&request=GetFeature&typename=${name}&outputFormat=application/json&propertyName=${field}&SORTBY=${field}&CQL_FILTER=${filtersUlr}`;

  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  try {
    let resp = await fetch(url, { signal });
    currentFetch = null;
    let total, arith, median, max, min;
    let data = await resp.json();
    let features = data.features;
    let values = features.map((feat) => feat.properties[field]);
    //Max
    max = values[values.length - 1];
    if (!Number.isInteger(max)) {
      max = max.toFixed(2);
    }
    //Min
    min = values[0];
    if (!Number.isInteger(min)) {
      min = min.toFixed(2);
    }
    //Total
    total = data.numberReturned;
    if (!total) {
      total = await getTableHits(layer, filtersUlr);
    }

    //Mitjana aritmètica
    arith = values.reduce((a, c) => a + c) / values.length;
    if (!Number.isInteger(arith)) {
      arith = arith.toFixed(2);
    } //Mediana
    if (values.length % 2 !== 0) {
      median = values[Math.round(values.length / 2) - 1];
    } else {
      let index1 = values.length / 2;
      let index2 = index1 - 1;
      median = (values[index1] + values[index2]) / 2;
    }
    if (!Number.isInteger(median)) {
      median = median.toFixed(2);
    }
    //Moda
    let hashMap = {};
    let maxCount = 0;
    let mode = [];
    values.forEach((num) => {
      hashMap[num] = hashMap[num] ? hashMap[num] + 1 : 1;
    });
    for (let num in hashMap) {
      if (hashMap[num] > maxCount) {
        maxCount = hashMap[num];
        mode = [num];
      } else if (hashMap[num] === maxCount) {
        mode.push(num);
      }
    }

    mode = mode.map(Number).map((num) => (Number.isInteger(num) ? num : num.toFixed(2)));

    if (mode.length === Object.keys(hashMap).length) {
      mode = ['--'];
    }

    return { total, arith, median, max, min, mode, values };
  } catch (error) {}
};

/**
 *
 * @param {*} conditions Condiciones
 * @param {*} operator Operador que actuará
 * @desc Función que convierte los filtros en una string válida como filtro CQL
 */
export const CQLStringMaker = (conditions, operator, layer) => {
  let string = ''; // se inicializa la string
  conditions.forEach((condition, index) => {
    // se recorre el array "conditions"
    let subString = ''; // se inicializa la substring para escribir una subcláusula en paréntesis
    if (condition.type === 'group') {
      // si es subcláusula
      const subGroupOperator = condition.groupOperator; // se obtiene el operador de la subcláusula
      // se llama recursivamente a la función para construir la substring entre paréntesis
      subString = `(${CQLStringMaker(condition.filters, subGroupOperator, layer)})`;

      if (index === 0) {
        // si el primer objeto es una subcláusula, pasamos el valor de la substring a la string principal
        string = subString;
      } else {
        // si no, se combina la substring con la string principal utilizando el operador proporcionado
        string = `${string} ${operator} ${subString}`;
      }
    } else {
      // si la condición es global
      let fieldString = ''; // se inicializa la substring para la condición global

      let dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$|^\d{2}-\d{2}-\d{4}$/;
      let value = isNaN(Number(condition.value)) && !dateRegex.test(condition.value) ? `'${condition.value}'` : condition.value;

      // se recorren las propiedades de la condición (Campo, operador y valor)
      for (let key in condition) {
        if (key === 'field') {
          // si la propiedad es "field", se obtiene el nombre del campo de la feature
          let properties = layer.getProperties();
          fieldString = properties.find((prop) => prop.alias === condition[key])?.name || condition[key];
          // fieldString = condition[key];
        } else if (key === 'operator') {
          if (condition.operator !== '') {
            // si el operador comienza con 'I' o 'N' (ILIKE, NOT ILIKE, etc.), se usa el valor sin comillas
            if (condition.operator.charAt(0) === 'I' || condition.operator.charAt(0) === 'N') {
              value = condition.value;
            }
            // si el valor contiene comillas simples, se reemplazan por dos comillas simples para evitar errores en la string de filtro CQL
            if (value.includes("'")) value = value.replace(/'/g, "''");
            // se construye la substring para la condición global, reemplazando "value" en el objeto de operadores con el valor proporcionado
            fieldString = `${fieldString} ${condition.operator.replace('value', value)}`;
          }
        }
      }

      if (index === 0) {
        // si es la primera condición, se asigna la substring al valor de la string principal
        string = fieldString;
      } else {
        // si no, se combina la substring con la string principal utilizando el operador proporcionado
        string = `${string} ${operator} ${fieldString}`;
      }
    }
  });
  // Retorna la string de filtro CQL resultante
  return string;
};

export const createGraph = (values, that) => {
  const x = values.reduce((a, c) => {
    a[c] = (a[c] || 0) + 1;
    return a;
  }, {});

  const entries = Object.entries(x);
  entries.sort((a, b) => a[0] - b[0]);
  const data = entries.map(([x, y]) => ({ x: Number(x), y }));

  const tooltipText = (props) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ backgroundColor: '#333333', padding: '5px' }}>
          <div>
            Valor de {that.props.statsValue}: {data.x}
          </div>
          <div>Número d'elements: {data.y}</div>
        </div>
      );
    }
    return null;
  };
  const formatAxis = (tickItem) => {
    tickItem = Number(tickItem);
    if (!Number.isInteger(tickItem)) return tickItem.toFixed(2);
    return tickItem;
  };

  let mode, arith, median;
  mode = that.state.mode;
  arith = Number(that.state.arith);
  median = Number(that.state.median);

  return (
    <ResponsiveContainer width="100%" height={window.innerWidth > 768 ? that.props.alturaInfoPunt - 60 : 200}>
      <LineChart
        data={data}
        x="x"
        y="y"
        margin={{
          top: 5,
          right: 10,
          left: 5,
          bottom: 5
        }}>
        <XAxis type="number" dataKey="x" stroke="white" fontSize={12} tickFormatter={formatAxis} domain={[data[0].x, data[data.length - 1].x]} />
        <YAxis type="number" dataKey="y" stroke="white" fontSize={12} />
        <Tooltip content={tooltipText} />
        <Line type="monotone" dataKey="y" stroke="#94d7e4" dot={false} strokeWidth={2} />
        {mode.map((mode) => {
          return <ReferenceLine x={mode} stroke="#f09400" strokeWidth={2} />;
        })}
        <ReferenceLine x={median} stroke="#94b651" strokeWidth={2} />
        <ReferenceLine x={arith} stroke="#d63ce7" strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
};

export const getAtributsTaula = (taulaLayerProperties, taulaArrayKeys) => {
  let atributsTaula = [];
  if (taulaLayerProperties)
    for (let property of taulaLayerProperties) {
      let key = property.name;
      if (taulaArrayKeys.includes(key)) {
        atributsTaula.push({ alias: property.alias, name: key });
      }
    }

  for (let key of taulaArrayKeys) {
    if (!atributsTaula.find((atribut) => atribut.name === key)) {
      atributsTaula.push({
        alias: key,
        name: key
      });
    }
  }

  return atributsTaula;
};

export const orderByProperties = (taulaArrayElement, properties) => {
  let orderedObj = {};
  for (let property of properties) {
    let key = property.name;
    if (taulaArrayElement.hasOwnProperty(key)) {
      orderedObj[key] = taulaArrayElement[key];
    }
  }

  for (let key of Object.keys(taulaArrayElement)) {
    if (!orderedObj.hasOwnProperty(key)) {
      orderedObj[key] = taulaArrayElement[key];
    }
  }
  return orderedObj;
};
